import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { PeopleMembersAnalyticsEventProperties } from 'src/app/PeopleMembers/PeopleMembers'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import BoxAndLinks from 'src/components/BoxAndLinks/BoxAndLinks'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import PeopleBox from 'src/components/PeopleBox/PeopleBox'
// import SvgDownload from 'src/components/Svgs/SvgDownload'
import SvgExpand from 'src/components/Svgs/SvgExpand'
import TestId from 'src/components/TestId/TestId'
import { OrganizationRoles } from 'src/enums'
// import { PeopleSubSections } from 'src/enums'
// import { useBackgroundDispatch } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { OrgInviteModel, UserConnectionModel } from 'src/models/people.models'
import { UserListModel } from 'src/models/user.models'
import {
  invitePeopleUrl,
  peopleInvitedToOrgUrl,
  peopleMembersUrl,
} from 'src/navigation/urls'
import { SearchQueryParams } from 'src/redux/createRootReducer'
// import { postPeopleDownload } from 'src/redux/actions/data/reportDownloads.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectInvitedToOrgListModelsFromState } from 'src/redux/selectors/data/invitedToOrg.selectors'
import { selectMembersListModelsFromState } from 'src/redux/selectors/data/members.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

interface PeopleBoxAndLinksProps {
  contactListModels?: UserConnectionModel[]
  loading?: boolean
  peopleInfiniteScrollProps: PeopleInfiniteScrollProps
  noPeopleComponent?: React.ReactElement
  showFilters?: boolean
  stateQueryParams: SearchQueryParams
  setStateQueryParams: (SearchQueryParams: SearchQueryParams) => void
  keywordFilter?: string
  setKeywordFilter?: (keyword: string) => void
  organizationRolesFilter?: Set<string>
  setOrganizationRoleFilter?: (organizationRoles: Set<string>) => void
  hideDeactivatedUsers?: boolean
  setHideDeactivatedUsers?: (hideDeactivatedUsers: boolean) => void
  handleOrganizationRoleSelection?: (role: string) => void
  setSort: (sortField: string) => void
  sortBy?: string
  sortDescending?: boolean
  initialLoad: boolean
  setInitialLoad: (initialLoad: boolean) => void
}

export interface PeopleFilters {
  keyword: string
  organizationRoles: OrganizationRoles[]
  showDeactivatedUsers?: boolean
  sortBy?: string
  sortDescending: boolean
}

export interface PeopleFiltersBody {
  search?: string
  organizationRoles?: string[]
  sortField?: string
  page?: number
  sortDirection?: string
  showDeactivatedUsers?: boolean
}

export interface PeopleInfiniteScrollProps {
  fetch: (filtersBody: PeopleFiltersBody, page?: number) => void
  page?: number
  loading: boolean
  complete: boolean
}

const PeopleBoxAndLinks: React.FC<PeopleBoxAndLinksProps> = (props) => {
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const memberListModels = useSelector(selectMembersListModelsFromState)
  const invitedListModels = useSelector(selectInvitedToOrgListModelsFromState)
  const user = useSelector(selectUserListModelFromState)

  const hasOrgAdminPermissions = user?.hasOrgAdminPermissions

  const { analytics } = useAnalyticsContext()

  const [selectedFilters, setSelectedFilters] = useState<
    PeopleMembersAnalyticsEventProperties | undefined
  >(undefined)

  // const dispatch = useBackgroundDispatch()
  const handlePageLocation = (label: string): string => {
    return matchPath(calculatedBackgroundLocation.pathname, peopleMembersUrl())
      ? 'organization members - ' + label + ' - text link press'
      : matchPath(
            calculatedBackgroundLocation.pathname,
            peopleInvitedToOrgUrl(),
          )
        ? 'invited to org - ' + label + ' - text link press'
        : label + ' - text link press'
  }

  const people = ():
    | UserListModel[]
    | UserConnectionModel[]
    | OrgInviteModel[] => {
    return matchPath(calculatedBackgroundLocation.pathname, peopleMembersUrl())
      ? Object.values(memberListModels)
      : Object.values(invitedListModels)
  }

  // const reportType = (): PeopleSubSections => {
  //   switch (calculatedBackgroundLocation.pathname) {
  //     case peopleMembersUrl():
  //     default:
  //       return PeopleSubSections.members
  //     case peopleContactsUrl():
  //       return PeopleSubSections.contacts
  //     case peopleInvitedToOrgUrl():
  //       return PeopleSubSections.invites
  //   }
  // }

  // const downloadPeopleString = (): string => {
  //   switch (calculatedBackgroundLocation.pathname) {
  //     case peopleContactsUrl():
  //       return 'contacts'
  //     case peopleInvitedToOrgUrl():
  //       return 'invitees'
  //     case peopleMembersUrl():
  //     default:
  //       return 'members'
  //   }
  // }

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track('org members filter applied', {
        role: selectedFilters.organizationRoles,
      })
    }
  }, [selectedFilters])

  return (
    <BackgroundPageContent noStackingIndex>
      {people().length > 0 ? (
        <BoxAndLinks
          links={
            hasOrgAdminPermissions
              ? [
                  <TestId testId="invitePeople" key={0}>
                    <HoverableLink
                      css={css`
                        display: flex;
                        align-items: center;
                        float: right;
                      `}
                      to={invitePeopleUrl()}
                      ariaLabelSource={'Invite People'}
                      onClick={(): void => {
                        analytics?.track(handlePageLocation('invite people'), {
                          intent: 'invite people',
                          interaction: 'button press',
                        })
                      }}
                    >
                      <Trns source={'Invite People'} />
                      <SvgExpand
                        css={css`
                          transform: scale(calc(14 / 24));
                        `}
                      />
                    </HoverableLink>
                  </TestId>,
                ]
              : []
          }
        >
          <PeopleBox
            peopleInfiniteScrollProps={props.peopleInfiniteScrollProps}
            loading={props.loading}
            showFilters={props.showFilters}
            stateQueryParams={props.stateQueryParams}
            setStateQueryParams={props.setStateQueryParams}
            keywordFilter={props.keywordFilter}
            setKeywordFilter={props.setKeywordFilter}
            organizationRolesFilter={props.organizationRolesFilter}
            setOrganizationRoleFilter={props.setOrganizationRoleFilter}
            handleOrganizationRoleSelection={
              props.handleOrganizationRoleSelection
            }
            hideDeactivatedUsers={props.hideDeactivatedUsers}
            setHideDeactivatedUsers={props.setHideDeactivatedUsers}
            setSort={props.setSort}
            sortBy={props.sortBy}
            sortDescending={props.sortDescending}
            initialLoad={props.initialLoad}
            setInitialLoad={props.setInitialLoad}
            setSelectedFilters={setSelectedFilters}
          />
        </BoxAndLinks>
      ) : props.loading ? (
        <div
          css={css`
            display: flex;
            align-items: center;
            width: 100%;
            flex-grow: 2;
          `}
        >
          <LoadingBackgroundPage />
        </div>
      ) : (
        props.noPeopleComponent
      )}
    </BackgroundPageContent>
  )
}

export default PeopleBoxAndLinks
